import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { axios_tenant } from "../../../../axios";

const GATEWAY_URL        = process.env.REACT_APP_API_BASE_URL;
const FALLBACK_DELAY_MS  = 15_000;
const POLL_INTERVAL_MS   = 7_000;
const JOIN_CONFIRM_MS    = 5_000;  // if server doesn't confirm job:join within this time, start polling

// Server sends phase as { index, total, key, label } (sometimes already flattened to a
// string) — the single source of truth for turning it into the label string that
// row.phase is always stored/rendered as.
const toPhaseLabel = (phase) => (phase && typeof phase === "object" ? phase.label : phase);

// Single source of truth for writing into `rows` state, used by the live socket path
// (applyProgress/applyRowError), the job:sync snapshot, and the polling fallback alike.
// A field-sparse update (e.g. a server row snapshot that doesn't carry fipId/remarks)
// must not clobber a value already known client-side, so every field falls back to prevRow.
const mergeRow = (prevRow, row) => ({
  ...prevRow,
  ...row,
  fileNo:  row.fileNo  || prevRow?.fileNo,
  fipId:   row.fipId   || row.data?.fipIdValue || (row.fileNo && !prevRow?.fipId ? row.fileNo : prevRow?.fipId),
  phase:   toPhaseLabel(row.phase) || prevRow?.phase,
  status:  row.status  || prevRow?.status,
  remarks: row.remarks || row.data?.remarks || row.message || prevRow?.remarks,
});

const mergeRows = (rawRows = {}, prevRows = {}) =>
  Object.fromEntries(
    Object.entries(rawRows).map(([key, row]) => [key, row ? mergeRow(prevRows?.[key], row) : row])
  );

export default function useSocket(activeJobIdRef, onDone, onError, onInterrupted) {

  const socketRef             = useRef(null);
  const jobIdRef              = useRef(null);
  const jobTokenRef           = useRef(null);
  const pollIntervalRef       = useRef(null);
  const fallbackTimeoutRef    = useRef(null);
  const joinConfirmTimeoutRef = useRef(null);

  const onDoneRef        = useRef(onDone);
  onDoneRef.current       = onDone;
  const onErrorRef       = useRef(onError);
  onErrorRef.current      = onError;
  const onInterruptedRef = useRef(onInterrupted);
  onInterruptedRef.current = onInterrupted;

  const [rows,            setRows]            = useState({});
  const [progress,        setProgress]        = useState({});
  const [totalRows,       setTotalRows]       = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [jobJoined,       setJobJoined]       = useState(false);

  // Upsert a progress payload by rowIndex (idempotent — safe to replay).
  const applyProgress = useCallback((data) => {
    if (data.jobId && data.jobId !== activeJobIdRef.current) return;

    setProgress(data);

    if (data.totalRows !== undefined) {
      setTotalRows(data.totalRows);
      setRows(prev => {
        const next = { ...prev };
        for (let i = 1; i <= data.totalRows; i++) {
          if (!next[i]) next[i] = { status: "pending" };
        }
        return next;
      });
    }

    if (data.rowIndex != null && data.rowIndex > 0) {
      setRows(prev => ({
        ...prev,
        [data.rowIndex]: mergeRow(prev[data.rowIndex], {
          fileNo:  data.fileNo,
          fipId:   data.data?.fipIdValue || data.fileNo,
          phase:   data.phase,
          status:  data.status,
          remarks: data.remarks || data.data?.remarks,
        }),
      }));
    }
  }, [activeJobIdRef]);

  const applyRowError = useCallback((data) => {
    if (data.jobId && data.jobId !== activeJobIdRef.current) return;
    setRows(prev => ({
      ...prev,
      [data.rowIndex]: mergeRow(prev[data.rowIndex], {
        fileNo:  data.fileNo,
        phase:   "Failed",
        status:  "file_failed",
        remarks: data.remarks || data.message,
      }),
    }));
  }, [activeJobIdRef]);

  const stopPolling = useCallback(() => {
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
    clearTimeout(fallbackTimeoutRef.current);
    fallbackTimeoutRef.current = null;
    clearTimeout(joinConfirmTimeoutRef.current);
    joinConfirmTimeoutRef.current = null;
  }, []);

  // Poll GET /adim/matter-action/status/:jobId as a last-resort fallback.
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(async () => {
      const jobId    = jobIdRef.current;
      const jobToken = jobTokenRef.current;
      if (!jobId || !jobToken) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        return;
      }
      try {
        const { data } = await axios_tenant.get(
          `/adim/matter-action/status/${jobId}?jobToken=${encodeURIComponent(jobToken)}`
        );
        const { event, payload, rows: pollRows, totalRows: pollTotalRows } = data || {};

        // Apply full accumulated row state if the backend included it.
        if (pollTotalRows != null) {
          setTotalRows(pollTotalRows);
        }
        if (pollRows && typeof pollRows === "object") {
          setRows(prev => ({ ...prev, ...mergeRows(pollRows, prev) }));
        }

        if (!event || !payload) return;

        const eventKey = event.replace(/^adim\//, "");

        if (eventKey === "matter:progress") {
          applyProgress(payload);
        } else if (eventKey === "matter:error") {
          if (payload.rowIndex == null) {
            jobIdRef.current    = null;
            jobTokenRef.current = null;
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            onErrorRef.current?.(payload.message || payload.remarks || "An unexpected error occurred.");
          } else {
            applyRowError(payload);
          }
        } else if (eventKey === "matter:done") {
          jobIdRef.current    = null;
          jobTokenRef.current = null;
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          onDoneRef.current?.(payload);
        }
      } catch (err) {
        if (err.response?.status === 400) {
          // Job is permanently gone (server restarted) — stop polling immediately.
          jobIdRef.current    = null;
          jobTokenRef.current = null;
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          onInterruptedRef.current?.();
          return;
        }
        console.error("Poll error:", err.message);
      }
    }, POLL_INTERVAL_MS);
  }, [applyProgress, applyRowError]);

  const scheduleJoinConfirmTimeout = useCallback(() => {
    clearTimeout(joinConfirmTimeoutRef.current);
    joinConfirmTimeoutRef.current = setTimeout(() => {
      joinConfirmTimeoutRef.current = null;
      if (jobIdRef.current && !pollIntervalRef.current) {
        console.log("adim/job:joined not confirmed — starting polling fallback");
        startPolling();
      }
    }, JOIN_CONFIRM_MS);
  }, [startPolling]);

  useEffect(() => {

    const token = localStorage.getItem("TREXO_TENANT_AUTH_TOKEN");

    const s = io(GATEWAY_URL, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: { token }
    });

    socketRef.current = s;

    s.on("connect", () => {
      console.log("Socket connected:", s.id);
      setSocketConnected(true);
      // Cancel pending fallback — socket is back.
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
      if (jobIdRef.current && jobTokenRef.current) {
        console.log("Reconnect — rejoining job room:", jobIdRef.current);
        s.emit("adim/job:join", { jobId: jobIdRef.current, jobToken: jobTokenRef.current });
        scheduleJoinConfirmTimeout();
      }
    });

    s.on("disconnect", () => {
      console.log("Socket disconnected");
      setSocketConnected(false);
      setJobJoined(false);
      // Schedule polling fallback if a job is active and no timer is pending.
      if (jobIdRef.current && !fallbackTimeoutRef.current) {
        fallbackTimeoutRef.current = setTimeout(() => {
          fallbackTimeoutRef.current = null;
          if (!socketRef.current?.connected && jobIdRef.current) {
            console.log("Socket still down after fallback delay — starting polling");
            startPolling();
          }
        }, FALLBACK_DELAY_MS);
      }
    });

    s.on("adim/job:joined", (data) => {
      console.log("Joined job room:", data);
      clearTimeout(joinConfirmTimeoutRef.current);
      joinConfirmTimeoutRef.current = null;
      setJobJoined(true);
      // Socket is back and room is confirmed — stop polling.
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    });

    s.on("adim/job:sync", (data) => {
      console.log("Job sync received:", data);
      if (data.totalRows != null) setTotalRows(data.totalRows);
      if (data.rows && typeof data.rows === "object") {
        setRows(prev => ({ ...prev, ...mergeRows(data.rows, prev) }));
      }
    });

    s.on("adim/matter:progress", applyProgress);

    s.on("adim/matter:error", (data) => {
      if (data.jobId && data.jobId !== activeJobIdRef.current) return;
      if (data.rowIndex == null) {
        jobIdRef.current    = null;
        jobTokenRef.current = null;
        stopPolling();
        onErrorRef.current?.(data.message || data.remarks || "An unexpected error occurred.");
      } else {
        applyRowError(data);
      }
    });

    s.on("adim/matter:done", (data) => {
      console.log("Job finished:", data);
      jobIdRef.current    = null;
      jobTokenRef.current = null;
      stopPolling();
      onDoneRef.current?.(data);
    });

    s.on("adim/server:shutdown", () => {
      console.log("Server shutdown — job interrupted");
      jobIdRef.current    = null;
      jobTokenRef.current = null;
      stopPolling();
      onInterruptedRef.current?.();
    });

    s.on("adim/socket:ready", (data) => console.log("ADIM socket ready:", data));
    s.on("connect_error",     (err)  => console.error("Socket error:", err.message));

    return () => {
      stopPolling();
      s.disconnect();
      socketRef.current = null;
    };

  }, [scheduleJoinConfirmTimeout]);

  const resetRows = useCallback(() => {
    stopPolling();
    jobIdRef.current    = null;
    jobTokenRef.current = null;
    setRows({});
    setProgress({});
    setTotalRows(0);
    setJobJoined(false);
  }, [stopPolling]);

  const joinJob = useCallback((jobId, jobToken) => {
    const s = socketRef.current;
    if (!s) return;
    jobIdRef.current    = jobId;
    jobTokenRef.current = jobToken;
    setJobJoined(false);
    if (s.connected) {
      console.log("Joining job room:", jobId);
      s.emit("adim/job:join", { jobId, jobToken });
      scheduleJoinConfirmTimeout();
    }
  }, [scheduleJoinConfirmTimeout]);

  return { rows, progress, totalRows, joinJob, resetRows, socketConnected, jobJoined };
}
